import { Add16, Download16 } from '@carbon/icons-react';
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { UploadFileWithAttachment } from 'api/attachments';
import { Task, TaskAPI } from 'api/tasks';
import { Attachment } from 'components/atoms/attachment-gallery/attachment';
import { convertFileAttachmentToUploadFileWithAttachment } from 'components/molecules/attachments';
import {
  CPListOrGridSwitcher,
  CpListOrGridSwitcherType,
} from '../../cp-list-or-grid-switcher';
import {
  ActionsWrapper,
  AddItemButton,
  AddItemWrapper,
  IconWrapper,
  SectionName,
  SectionWrapper,
  TitleWrapper,
} from './components';

interface Props {
  task: Task;
}

const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-05);
  padding-left: var(--cp-spacing-05);
  padding-right: var(--cp-spacing-05);
  margin-bottom: var(--cp-spacing-06);

  @media (max-width: 480px) {
    flex-wrap: nowrap;
    overflow-x: auto;
    /* hide scrollbar */
    scrollbar-width: none;
    -ms-overflow-style: none;
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const GridView: React.FC<{
  attachmentFiles: UploadFileWithAttachment[];
  onRemoveFile: (file: UploadFileWithAttachment) => void;
  onDownloadFile: (file: UploadFileWithAttachment) => void;
}> = ({ attachmentFiles, onRemoveFile, onDownloadFile }) => {
  return (
    <Grid>
      {attachmentFiles.map((af) => (
        <Attachment
          isBasicPreview
          key={af?.attachment?.attachmentId}
          file={af}
          onRemoveFile={() => onRemoveFile(af)}
          onDownloadFile={() => onDownloadFile(af)}
          large
        />
      ))}
    </Grid>
  );
};

export const CpTaskAttachmentsSection: React.FC<Props> = ({ task }) => {
  const [attachments, setAttachments] = useState(() => task.attachments ?? []);
  const [viewType, setViewType] = useState<CpListOrGridSwitcherType>(
    CpListOrGridSwitcherType.GRID,
  );

  const attachmentFiles = useMemo(
    () => convertFileAttachmentToUploadFileWithAttachment(attachments),
    [attachments],
  );

  useEffect(() => {
    setAttachments(task.attachments ?? []);
  }, [task.attachments]);

  return (
    <SectionWrapper>
      <TitleWrapper>
        <SectionName>
          <FormattedMessage id="attachments" />
        </SectionName>
        <ActionsWrapper>
          <CPListOrGridSwitcher
            type={viewType}
            onChange={(type) => setViewType(type)}
          />
          <IconWrapper>
            <Download16 />
          </IconWrapper>
        </ActionsWrapper>
      </TitleWrapper>
      {attachments.length > 0 ? (
        <>
          {viewType === CpListOrGridSwitcherType.GRID ? (
            <GridView
              attachmentFiles={attachmentFiles}
              onRemoveFile={(file) => {
                const attachmentId = file.attachment?.attachmentId;

                if (attachmentId) {
                  setAttachments((prev) => {
                    return prev.filter((a) => a.attachmentId !== attachmentId);
                  });
                  TaskAPI.deleteTaskAttachment(attachmentId);
                }
              }}
              onDownloadFile={() => {}}
            />
          ) : (
            <></>
          )}
        </>
      ) : (
        <></>
      )}
      <AddItemWrapper>
        <Add16 />
        <AddItemButton>
          <FormattedMessage id="attachment.add" />
        </AddItemButton>
      </AddItemWrapper>
    </SectionWrapper>
  );
};
